import React from "react";
import { ArrowRight, MessageCircle } from "lucide-react";

import { Card, CardContent } from "./components/ui/card";
import { SiteHeader } from "./components/SiteHeader";
import { SiteFooter } from "./components/SiteFooter";
import { useDocumentMeta } from "./hooks/useDocumentMeta";
import { submitLeadWithWhatsApp } from "./lib/whatsappHandoff";
import { mediaSections } from "./mock/mediaMock";

const whatsappHref = "https://wa.me/918733085863";

const ForQCTeams = () => {
  useDocumentMeta({
    title: "PickQuick for Platforms | Regional Brand Depth",
    description:
      "PickQuick gives quick commerce platforms instant regional brand depth, clean operations and predictable monetisation across 52 Indian cities and 61 brand partners.",
    ogType: "website",
    ogUrl: "https://letspickquick.com/for-platforms",
    canonical: "https://letspickquick.com/for-platforms",
    keywords: "quick commerce platforms, regional brands quick commerce, blinkit partner, zepto partner, instamart partner, quick commerce monetisation, campaign budgets quick commerce, category growth quick commerce",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "PickQuick for Platforms, regional brand depth and monetisation for quick commerce",
        "serviceType": "Quick commerce platform enablement",
        "provider": {
          "@type": "Organization",
          "name": "PickQuick",
          "legalName": "Civicgrid Technologies Pvt. Ltd.",
          "url": "https://letspickquick.com"
        },
        "areaServed": { "@type": "Country", "name": "India" },
        "url": "https://letspickquick.com/for-platforms",
        "description":
          "PickQuick gives quick commerce platforms instant regional brand depth, clean operations and predictable monetisation across dozens of Indian cities."
      },
    ],
  });
  // NavLinks and site chrome are provided by <SiteHeader /> and <SiteFooter />.
  // All head/meta/JSON-LD is emitted by useDocumentMeta above; no <Helmet>.

  return (
    <div className="min-h-screen bg-[var(--bg-deep)] text-white flex flex-col relative">
      {/* WhatsApp floating button */}
      <a
        href={whatsappHref}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-5 right-5 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] shadow-lg hover:shadow-xl transition-shadow duration-200"
      >
        <MessageCircle className="h-6 w-6 text-white" />
      </a>

      <SiteHeader />

      {/* Main */}
      <main className="flex-1">
        <section className="pt-12 pb-10">
          <div className="container">
            <div className="max-w-[480px] mx-auto md:max-w-3xl md:mx-auto space-y-6 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--text-muted)]">
                For Quick Commerce Teams
              </p>
              <h1 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight">
                Build regional depth. Unlock stronger monetisation.
              </h1>
              <p className="text-base md:text-lg text-[var(--text-body)]">
                PickQuick manages hundreds of regional brands across India.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
                <a
                  href="https://calendar.app.google/q771ocWtwv6E75dr5"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                >
                  <span>Talk to a Brand Expert</span>
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Sub-headline */}
        <section className="py-10">
          <div className="container">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-base md:text-lg text-[var(--text-body)]">
                PickQuick gives QC teams instant city-level assortment readiness, clean operational execution, predictable advertising budgets, and consolidated brand management, all through one partner instead of hundreds of fragmented vendors.
              </p>
            </div>
          </div>
        </section>

        {/* Why QC Teams Work With PickQuick - 3 Column Cards */}
        <section className="py-16">
          <div className="container">
            <div className="max-w-3xl mx-auto mb-10 text-center">
              <h2 className="h2 mb-4 text-white">Why Quick Commerce Teams Work With PickQuick</h2>
              <p className="body-md">
                Quick Commerce growth depends on three things: regional depth, operational reliability, and predictable monetisation. Most vendors cannot deliver any of these consistently.
              </p>
            </div>
            
            <div className="grid gap-6 md:grid-cols-3">
              <Card className="feature-card bg-[var(--bg-elevated)] border-[var(--border)]">
                <CardContent className="pt-6">
                  <h3 className="h4 mb-3 text-white">One operator managing everything</h3>
                  <ul className="space-y-2 text-sm text-[var(--text-body)]">
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>Zero operational noise</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>Clean replenishment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>No WhatsApp groups</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="feature-card bg-[var(--bg-elevated)] border-[var(--border)]">
                <CardContent className="pt-6">
                  <h3 className="h4 mb-3 text-white">Predictable campaign budgets</h3>
                  <ul className="space-y-2 text-sm text-[var(--text-body)]">
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>Stronger ad spends</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>Quarterly commitments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>Consistent monetisation</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="feature-card bg-[var(--bg-elevated)] border-[var(--border)]">
                <CardContent className="pt-6">
                  <h3 className="h4 mb-3 text-white">City-level readiness</h3>
                  <ul className="space-y-2 text-sm text-[var(--text-body)]">
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>Instant assortment depth</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>High-trust local brands</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>Faster expansion cycles</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Regional Brand Network - Big Impact Card */}
        <section className="py-16">
          <div className="container">
            <div className="max-w-5xl mx-auto">
              <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] items-center">
                <div className="space-y-4 text-center lg:text-left">
                  <h2 className="h2 text-white">India's Deepest Regional Brand Network</h2>
                  <p className="body-md">
                    PickQuick holds operator rights to hundreds of regional consumer brands across India.
                  </p>
                  <p className="body-md font-semibold text-white">
                    When a platform enters a new city, and lists the top local brands, PickQuick already represents most of them.
                  </p>
                </div>
              
              <Card className="feature-card bg-[var(--bg-elevated)] border-[var(--border)]">
                <CardContent className="pt-6 space-y-3">
                  <p className="font-semibold text-white">This unlocks:</p>
                  <ul className="space-y-2 text-sm text-[var(--text-body)]">
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Deepest catalogue of local favourites</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Higher search-to-conversion rates</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Better retention</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Stronger city-level differentiation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Faster expansion cycles</span>
                    </li>
                  </ul>
                  <p className="font-semibold text-white pt-2">
                    No discovery phase. No vendor sourcing. Just plug-and-play regional depth.
                  </p>
                </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Monetisation - 3 Column Cards */}
        <section className="py-16">
          <div className="container">
            <div className="max-w-3xl mb-10 text-center mx-auto">
              <h2 className="h2 mb-4 text-white">More Monetisation. Larger Budgets. Easier Negotiation.</h2>
              <p className="body-md">
                Monetisation is a core pillar for every QC team, and PickQuick strengthens it dramatically.
              </p>
            </div>
            
            <div className="grid gap-6 md:grid-cols-3">
              <Card className="feature-card bg-[var(--bg-elevated)] border-[var(--border)]">
                <CardContent className="pt-6 space-y-3">
                  <h3 className="h4 text-white">Consolidated advertising budgets</h3>
                  <p className="text-sm text-[var(--text-body)]">
                    Because PickQuick represents hundreds of brands, ad budgets consolidate at the operator level.
                  </p>
                  <ul className="space-y-2 text-sm text-[var(--text-body)]">
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>Larger spends from single negotiation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>Better quarterly planning</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>Multi-brand campaign allocations</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="feature-card bg-[var(--bg-elevated)] border-[var(--border)]">
                <CardContent className="pt-6 space-y-3">
                  <h3 className="h4 text-white">Predictable quarterly commitments</h3>
                  <p className="text-sm text-[var(--text-body)]">
                    Unlike small vendors who fluctuate month to month, PickQuick commits quarterly and plans seasonally.
                  </p>
                  <ul className="space-y-2 text-sm text-[var(--text-body)]">
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>Quarterly commitments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>Seasonal planning</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>Budget discipline</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="feature-card bg-[var(--bg-elevated)] border-[var(--border)]">
                <CardContent className="pt-6 space-y-3">
                  <h3 className="h4 text-white">Stronger negotiation leverage</h3>
                  <p className="text-sm text-[var(--text-body)]">
                    With one agreement, QC teams unlock budgets across dozens of brands.
                  </p>
                  <ul className="space-y-2 text-sm text-[var(--text-body)]">
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>Campaign adoption</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>Premium placement</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--text-muted)] mt-1">•</span>
                      <span>AOP fulfilment</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="mt-10 max-w-3xl mx-auto">
              <div className="rounded-xl border border-[rgba(241, 89, 43,0.3)] bg-[rgba(241, 89, 43,0.05)] p-8 text-center">
                <p className="text-sm font-semibold text-white mb-2">Campaign Monetisation Engine</p>
                <p className="text-xs text-[var(--text-muted)]">
                  Regional Brands → PickQuick Budget Pool → Multi-Brand Campaigns → Higher Monetisation for QC Platforms
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What PickQuick Handles - 2 Column Layout */}
        <section className="py-16">
          <div className="container">
            <div className="max-w-5xl mx-auto">
              <div className="grid gap-10 lg:grid-cols-2">
              <Card className="feature-card bg-[var(--bg-elevated)] border-[var(--border)]">
                <CardContent className="pt-6 space-y-4">
                  <h2 className="h3 text-white">Before Inventory Reaches the Platform</h2>
                  <p className="text-sm text-[var(--text-body)]">
                    PickQuick prepares every brand end-to-end before go-live:
                  </p>
                  <ul className="space-y-2 text-sm text-[var(--text-body)]">
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Packaging compliance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Barcode + GS1 readiness</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Professional product imaging</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Listing + catalogue creation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Seller Hub readiness</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Replenishment setup and Min, Max planning</span>
                    </li>
                  </ul>
                  <p className="font-semibold text-white pt-2">
                    QC teams get ready-to-scale brands on Day 1.
                  </p>
                </CardContent>
              </Card>

              <Card className="feature-card bg-[var(--bg-elevated)] border-[var(--border)]">
                <CardContent className="pt-6 space-y-4">
                  <h2 className="h3 text-white">After Go-Live</h2>
                  <p className="text-sm text-[var(--text-body)]">
                    PickQuick manages complete post-live operations:
                  </p>
                  <ul className="space-y-2 text-sm text-[var(--text-body)]">
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Replenishment & Min, Max optimisation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>RO discipline</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Dark store availability improvements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Multi-city expansion</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Ad optimisation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[var(--accent)] mt-1">✓</span>
                      <span>Reporting & insight delivery</span>
                    </li>
                  </ul>
                  <p className="font-semibold text-white pt-2">
                    Category stability, predictable stock flow, consistent availability.
                  </p>
                </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* When to Work With PickQuick - Centered Card */}
        <section className="py-16">
          <div className="container">
            <div className="max-w-5xl mx-auto">
              <Card className="feature-card bg-[var(--bg-elevated)] border-[var(--border)]">
                <CardContent className="pt-6 space-y-4">
                  <h2 className="h3 mb-4 text-center text-white">When Quick Commerce Teams Should Work With PickQuick</h2>
                  <p className="text-sm text-[var(--text-body)] text-center mb-6">
                    PickQuick is best suited when QC teams want to:
                  </p>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="text-[var(--accent)] mt-1 font-bold">✓</span>
                        <span className="text-sm text-[var(--text-body)]">Launch a new city quickly</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[var(--accent)] mt-1 font-bold">✓</span>
                        <span className="text-sm text-[var(--text-body)]">Strengthen regional depth</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[var(--accent)] mt-1 font-bold">✓</span>
                        <span className="text-sm text-[var(--text-body)]">Improve availability in high-frequency categories</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[var(--accent)] mt-1 font-bold">✓</span>
                        <span className="text-sm text-[var(--text-body)]">Expand store coverage</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="text-[var(--accent)] mt-1 font-bold">✓</span>
                        <span className="text-sm text-[var(--text-body)]">Accelerate monetisation</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[var(--accent)] mt-1 font-bold">✓</span>
                        <span className="text-sm text-[var(--text-body)]">Remove vendor-level operational noise</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[var(--accent)] mt-1 font-bold">✓</span>
                        <span className="text-sm text-[var(--text-body)]">Tap into pre-existing regional brand trust</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[var(--accent)] mt-1 font-bold">✓</span>
                        <span className="text-sm text-[var(--text-body)]">Scale city-by-city with high-repeat products</span>
                      </div>
                    </div>
                  </div>
                  <p className="font-semibold text-white text-center mt-6">
                    If the goal is category acceleration, city-level depth, and predictable monetisation, PickQuick is the most efficient partner.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-16">
          <div className="container">
            <div className="max-w-[480px] mx-auto md:max-w-2xl md:mx-auto">
              <Card className="feature-card bg-[var(--bg-elevated)] border-[var(--border)]">
                <CardContent className="pt-6 space-y-4">
                  <div className="text-center space-y-2">
                    <h2 className="h3 text-white">Ready to scale regional depth and monetisation?</h2>
                    <p className="text-sm text-[var(--text-muted)]">
                      Share your details and we'll align on platforms, categories and timelines.
                    </p>
                  </div>
                  
                  <form className="space-y-4" onSubmit={(e) => {
                    e.preventDefault();
                    const form = e.target;
                    const formData = new FormData(form);
                    const data = {
                      name: formData.get('name'),
                      brand: formData.get('brand'),
                      email: formData.get('email'),
                      phone: formData.get('phone'),
                      message: formData.get('message')
                    };

                    // WhatsApp is the primary action; the backend POST fires
                    // silently and never blocks or reverses the handoff.
                    submitLeadWithWhatsApp({
                      payload: {
                        name: data.name,
                        brand: data.brand,
                        email: data.email,
                        phone: data.phone,
                        context: data.message,
                        product_category: 'QC Teams',
                        lead_type: 'qc-teams',
                        source: 'qc-teams-form',
                      },
                      whatsapp: {
                        title: 'New PickQuick For Platforms enquiry',
                        source: 'qc-teams-form',
                        pagePath: '/for-quick-commerce',
                        fields: {
                          'Name': data.name,
                          'Platform / Organisation': data.brand,
                          'Work email': data.email,
                          'Phone': data.phone || '(not provided)',
                          'Message': data.message || '(none)',
                        },
                      },
                    });

                    form.reset();
                  }}>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <label className="form-label text-xs text-[var(--text-muted)]" htmlFor="qc-name">Name *</label>
                        <input
                          id="qc-name"
                          name="name"
                          required
                          className="input-field mt-1 w-full"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="form-label text-xs text-[var(--text-muted)]" htmlFor="qc-brand">Platform/Organization *</label>
                        <input
                          id="qc-brand"
                          name="brand"
                          required
                          className="input-field mt-1 w-full"
                          placeholder="Platform name"
                        />
                      </div>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <label className="form-label text-xs text-[var(--text-muted)]" htmlFor="qc-email">Work Email *</label>
                        <input
                          id="qc-email"
                          name="email"
                          type="email"
                          required
                          className="input-field mt-1 w-full"
                          placeholder="name@platform.com"
                        />
                      </div>
                      <div>
                        <label className="form-label text-xs text-[var(--text-muted)]" htmlFor="qc-phone">Phone</label>
                        <input
                          id="qc-phone"
                          name="phone"
                          className="input-field mt-1 w-full"
                          placeholder="With country code"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="form-label text-xs text-[var(--text-muted)]" htmlFor="qc-message">Message</label>
                      <textarea
                        id="qc-message"
                        name="message"
                        className="input-field mt-1 w-full min-h-[80px]"
                        placeholder="Tell us about your requirements..."
                      />
                    </div>
                    <button
                      type="submit"
                      data-testid="qc-teams-form-submit"
                      className="btn-primary w-full inline-flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="h-4 w-4" />
                      <span>Send on WhatsApp</span>
                    </button>
                  </form>
                  
                  <div className="text-center pt-4">
                    <p className="text-xs text-[var(--text-muted)] mb-3">Or schedule a call directly:</p>
                    <a
                      href="https://calendar.app.google/q771ocWtwv6E75dr5"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary inline-flex items-center gap-2"
                    >
                      <span>Talk to a Brand Expert</span>
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
};

export default ForQCTeams;
